"use client";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { LoaderCircleIcon } from "lucide-react";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full md:rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-secondary hover:text-secondary-foreground",
        secondary: "bg-gray-100  hover:bg-gray-200 active:bg-gray-3000",
        ghost: "hover:bg-secondary hover:text-secondary-foreground",
        link: "text-link underline-offset-4 hover:text-link-hover hover:underline",
        light: "bg-primary/10 hover:bg-primary/20",
        // Carries its own focus ring because the shared base only clears the
        // native outline without replacing it.
        warning:
          "bg-orange-700 text-white hover:bg-orange-800 focus-visible:ring-2 focus-visible:ring-orange-700 focus-visible:ring-offset-2",
        success:
          "bg-green-700 text-white hover:bg-green-800 focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2",
        white_shadowed: cn(
          "rounded-sm border bg-card text-card-foreground shadow-md",
          "focus:outline-none focus:ring-1 focus:ring-ring hover:bg-slate-100",
          "active:bg-blue-50 active:ring-blue-200 active:ring-1 active:shadow-none active:scale-[99%]",
          "transition-[transform,box-shadow,background] duration-100",
        ),
        custom: "",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-6 md:rounded-md px-2",
        sm: "h-9 md:rounded-md px-3",
        lg: "h-11 md:rounded-md px-8",
        icon: "h-10 w-10",
        inline: "h-auto px-0 py-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingSection?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading,
      loadingSection,
      disabled,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        onClick={loading || disabled ? undefined : onClick}
        className={cn(buttonVariants({ size, className, variant }))}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {loading ? (
          <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          loadingSection
        )}
        {asChild ? <Slottable>{children}</Slottable> : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
